import type { ReactNode } from 'react';
import type { StaticContent, StaticListBlock, StaticListStyle } from '../types';

const LIST_STYLE_CLASS: Record<StaticListStyle, string> = {
  decimal: 'ol-decimal',
  'lower-alpha': 'ol-lower-alpha',
  'lower-roman': 'ol-lower-roman',
};

type StaticListProps = {
  block: StaticListBlock;
  renderContent: (content: StaticContent) => ReactNode;
  'data-testid'?: string;
};

/**
 * Nested list block used inside static page JSON.
 *
 * Supports optional intro and outro copy around a styled ordered list.
 * Items may themselves be strings, nested lists, or other content types;
 * rendering is delegated through `renderContent` so nesting stays recursive.
 *
 * @param block - List data from JSON (`style` defaults to decimal)
 * @param renderContent - Renderer for each item, typically {@link StaticContentRenderer}
 */
function StaticList({ block, renderContent, 'data-testid': dataTestId = 'static-list' }: StaticListProps) {
  const style = block.style ?? 'decimal';

  return (
    <>
      {block.intro ? renderContent(block.intro) : null}
      <ol className={LIST_STYLE_CLASS[style]} data-testid={dataTestId}>
        {block.items.map((item, index) => (
          <li key={index}>{renderContent(item)}</li>
        ))}
      </ol>
      {block.outro ? renderContent(block.outro) : null}
    </>
  );
}

export { StaticList, type StaticListProps };
